/**
 * Selector de convenio: elegir, crear y borrar.
 *
 * Componente presentacional — todo el estado (lista, selección, formulario de
 * alta, confirmación de borrado) vive en `PolicyAdmin` (orquestador). Este
 * fichero solo pinta la barra y delega en los callbacks del padre.
 */
import { WorkPolicyView } from '../../../services/api/workPolicies';

interface PolicySelectorProps {
  policies: WorkPolicyView[];
  selectedId: number | null;
  policy: WorkPolicyView | null;
  onSelect: (id: number) => void;

  creating: boolean;
  onToggleCreating: () => void;
  newName: string;
  onNewNameChange: (value: string) => void;
  onSubmitCreate: () => void;
  creatingBusy: boolean;

  confirmDelete: boolean;
  deleting: boolean;
  onDeleteClick: () => void;
  onCancelDelete: () => void;
}

export function PolicySelector({
  policies,
  selectedId,
  policy,
  onSelect,
  creating,
  onToggleCreating,
  newName,
  onNewNameChange,
  onSubmitCreate,
  creatingBusy,
  confirmDelete,
  deleting,
  onDeleteClick,
  onCancelDelete,
}: PolicySelectorProps) {
  return (
    <div className="card-surface p-5 space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="label">Convenio</label>
          <select
            className="field"
            value={selectedId ?? ''}
            onChange={(e) => onSelect(Number(e.target.value))}
          >
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.is_default ? ' (por defecto)' : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={onToggleCreating}
          className="btn-ghost h-12 w-auto px-5 shrink-0"
        >
          {creating ? 'Cancelar' : '+ Nuevo convenio'}
        </button>
        <button
          type="button"
          onClick={onDeleteClick}
          disabled={!policy || policy.is_default || deleting}
          className="h-12 px-5 rounded-xl border border-danger text-danger font-semibold text-[14px] disabled:opacity-40 shrink-0"
          title={policy?.is_default ? 'El convenio por defecto no se puede borrar' : undefined}
        >
          {deleting ? 'Borrando…' : confirmDelete ? '¿Seguro? Sí, borrar' : 'Borrar convenio'}
        </button>
        {confirmDelete && (
          <button
            type="button"
            onClick={onCancelDelete}
            className="btn-ghost h-12 w-auto px-4 shrink-0"
          >
            Cancelar
          </button>
        )}
      </div>

      {creating && (
        <div className="flex flex-wrap items-end gap-2.5 rounded-xl bg-surface-2 p-3.5 animate-rise">
          <div className="flex-1 min-w-[200px]">
            <label className="label">Nombre del nuevo convenio</label>
            <input
              className="field"
              value={newName}
              onChange={(e) => onNewNameChange(e.target.value)}
              placeholder="p. ej. Convenio hostelería"
              autoFocus
            />
          </div>
          <button
            type="button"
            onClick={onSubmitCreate}
            disabled={!newName.trim() || creatingBusy}
            className="h-12 px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px] disabled:opacity-40 shrink-0"
          >
            {creatingBusy ? 'Creando…' : 'Crear'}
          </button>
        </div>
      )}
    </div>
  );
}
